"use client";

import { storyblokEditable } from "@storyblok/react/rsc";
import { cn } from "@/lib/utils";
import { CardStoryblok } from "@/component-types-sb";
import Image from "next/image";
import Title from "@/components/Title";
import Button from "@/components/Button";
import Link from "next/link";
import { getStoryblokImageUrl } from "@/lib/storyblok-image";

const Card: React.FC<{
  blok: CardStoryblok;
  isLoading?: boolean;
  fullWidth?: boolean;
  aspectRatio?: number;
}> = ({ blok, isLoading = false, fullWidth = true, aspectRatio }) => {
  const isEvent = blok.variant === "event";

  let cardInnerContent = null;
  if (isEvent) {
    cardInnerContent = (
      <div className={cn("text-white flex flex-col items-center justify-center h-full gap-2 w-full transition-all duration-500", blok.link && "group-hover:text-transparent")}>
        <p className="text-xl md:text-2xl mb-2">
          {formatDateTimeString(blok.date)}
        </p>
        <Title variant="h2" className="text-center">
          {blok.title}
        </Title>
        {!blok.link &&
          blok.buttons?.map((button) => (
            <Button
              key={button._uid}
              blok={{
                _uid: "button_" + button._uid,
                component: "button",
                title: button.title,
                variant: button.variant,
                type: button.type,
              }}
              className="w-fit"
            />
          ))}
      </div>
    );
  } else {
    cardInnerContent = (
      <div className="text-white flex flex-col h-full justify-end gap-4 md:max-w-1/2">
        {blok.date && (
          <p className="text-xl md:text-2xl mb-2">
            {formatDateTimeString(blok.date, false)}
          </p>
        )}
        <Title variant="h2">{blok.title}</Title>
        <p>{blok.description}</p>
        {!blok.link &&
          blok.buttons?.map((button) => (
            <Button
              key={button._uid}
              blok={{
                _uid: "button_" + button._uid,
                component: "button",
                title: button.title,
                variant: button.variant,
                type: button.type,
              }}
              className="w-fit"
            />
          ))}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        {...storyblokEditable(blok)}
        className={cn(
          "relative w-full rounded-lg overflow-hidden group",
          "lg:aspect-[1.5/1] h-full min-h-100 lg:h-100",
          fullWidth && "md:col-span-2"
        )}
      >
        <div className="bg-gray-300 animate-pulse absolute inset-0"></div>
        <div className="flex flex-col justify-end p-6 relative h-full inset-0">
          <div className="text-white flex flex-col h-full gap-4">
            <div className="h-4 bg-gray-400 rounded w-1/3 animate-pulse mb-2"></div>
            <div className="flex-grow">
              <div className="h-6 bg-gray-400 rounded w-2/3 animate-pulse"></div>
            </div>
            <div className="h-10 bg-gray-400 rounded-full w-28 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const cardContent = (
    <>
      {blok.image && (
        isEvent ? (
          <Image
            src={getStoryblokImageUrl(blok.image.filename, "600x0/filters:quality(75)")}
            alt={String(blok.image.title || blok.title)}
            width={600}
            height={400}
            sizes="(max-width: 768px) 100vw, 25vw"
            className={`w-full h-auto object-cover ${
              blok.link && "group-hover:scale-103 transition-transform duration-500 ease-in-out"
            }`}
            loading="lazy"
          />
        ) : (
          <Image
            src={getStoryblokImageUrl(
              blok.image.filename,
              `${fullWidth ? "1600x0" : "800x0"}/filters:quality(75)`
            )}
            alt={String(blok.image.title || blok.title)}
            fill
            sizes={blok.fullWidth ? "100vw" : "(max-width: 768px) 100vw, 50vw"}
            className={cn(
              "object-cover",
              blok.link && "group-hover:scale-105 transition-transform duration-500 ease-in-out"
            )}
            loading="lazy"
          />
        )
      )}
      {/* Overlay for gradient darkening, fades out on hover (events only) */}
      {isEvent && (
        <div
          className={cn("absolute inset-0 pointer-events-none transition-opacity duration-500 bg-gradient-to-b from-black/10 via-black/80 to-black/10 opacity-100", blok.link && "group-hover:opacity-0")}
        />
      )}
      <div
        className={cn(
          isEvent
            ? "absolute inset-0 flex flex-col items-center justify-center p-4 w-full h-full"
            : cn("relative h-full min-h-[inherit] inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/30 flex flex-col justify-end p-6 transition-all duration-500", blok.link && "group-hover:bg-transparent group-hover:text-transparent")
        )}
      >
        {cardInnerContent}
      </div>
    </>
  );

  let cardClass = "relative w-full rounded-lg overflow-hidden group ";
  if (!isEvent) {
    cardClass += `lg:aspect-[1.5/1] h-full min-h-100 lg:h-100 ${fullWidth ? "md:col-span-2" : ""}`;
  } else if (!aspectRatio) {
    // Event cards get their height from the image's aspect ratio; without an
    // image there is nothing to derive it from and the content is absolutely
    // positioned, so fall back to a fixed ratio to keep the card visible.
    cardClass += "aspect-[3/2]";
  }
  if (blok.link) {
    cardClass += " cursor-pointer";
  }
  const cardStyle = isEvent && aspectRatio ? { aspectRatio: `1/${aspectRatio}` } : undefined;
  return (
    <div
      {...storyblokEditable(blok)}
      className={cardClass}
      style={cardStyle}
    >
      {blok.link ? (
        <Link href={blok.link || "#"} className="block w-full h-full min-h-[inherit]">
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
    </div>
  );
};

function formatDateTimeString(dateInput?: string, includeTime = true): string {
  const dateObj = dateInput ? new Date(dateInput) : new Date();
  const dateStr = dateObj.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  if (!includeTime) return dateStr;
  const pad = (n: number) => n.toString().padStart(2, "0");
  const timeStr = `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
  return `${dateStr}, ${timeStr}`;
}

export default Card;
